import { format, parseISO } from 'date-fns';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

/**
 * Formate une date selon le format spécifié
 * @param date Date à formater (Date, ISO string, ou timestamp)
 * @param formatStr Format de sortie (par défaut: 'dd/MM/yyyy')
 * @returns Date formatée sous forme de chaîne
 */
export const formatDate = (
  date: Date | string | number,
  formatStr = 'dd/MM/yyyy',
): string => {
  if (!date) return '';

  try {
    // Si c'est une chaîne ISO, la convertir d'abord
    if (typeof date === 'string' && date.includes('T')) {
      return format(parseISO(date), formatStr);
    }

    // Sinon, utiliser directement
    return format(typeof date === 'number' ? new Date(date) : date, formatStr);
  } catch (error) {
    console.error('Error formatting date:', error);
    return String(date);
  }
};

/**
 * Convertit une date au format relatif (il y a X minutes, heures, etc.)
 * @param date Date à convertir
 * @returns Chaîne au format relatif
 */
export const getRelativeTime = (date: Date | string | number): string => {
  if (!date) return '';

  try {
    const now = new Date();
    const targetDate =
      typeof date === 'string' && date.includes('T')
        ? parseISO(date)
        : new Date(date);

    const diffMs = now.getTime() - targetDate.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSecs < 60) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 30) return `${diffDays}d ago`;

    return formatDate(targetDate); // dd/MM/yyyy
  } catch (error) {
    console.error('Error getting relative time:', error);
    return String(date);
  }
};
export const formatDateString = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString();
};

export const formatDisplayDate = (dateString: string): string => {
  if (!dateString) return '-';

  return dayjs(dateString).format('DD/MM/YYYY HH:mm:ss');
};

export const formatCampaignDate = (
  dateString: string,
  isRevertForm?: boolean,
): string => {
  if (!dateString) return '-';

  const d = dayjs(dateString);

  return isRevertForm ? d.format('YYYY-MM-DD') : d.format('DD/MM/YYYY');
};

export const formatTimeFromISO = (isoString: string): string => {
  const date = new Date(isoString);

  let hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';

  hours = hours % 12;
  hours = hours ? hours : 12;

  const paddedHours = hours.toString().padStart(2, '0');
  const paddedMinutes = minutes.toString().padStart(2, '0');

  return `${paddedHours}:${paddedMinutes} ${ampm}`;
};

export const formatDateTime = (dateString: string) => {
  if (!dateString) return '-';
  return dayjs(dateString).utc().format('DD/MM/YYYY HH:mm [UTC]');
};

/**
 * Convert a `Date` to a `YYYY-MM-DD` calendar string for the analytics API.
 *
 * Uses the user's LOCAL timezone (matches what they see in the date picker),
 * so picking "May 26" always sends `2026-05-26` regardless of TZ. The backend
 * currently interprets these strings as UTC midnight, which means events at
 * the very edges of a day can land in an adjacent UTC bucket for users far
 * from UTC. A proper TZ-aware fix would require sending the user's timezone
 * offset alongside and adjusting the backend's `$gte` / `$lte` predicates.
 * Until then, this util is the single source of truth for the FE→BE date
 * encoding so a future refactor only has to touch one place.
 */
export const toCalendarDateString = (date: Date): string =>
  format(date, 'yyyy-MM-dd');
